/**
 * @module howLongAgo
 * @since 2.0.0
 */

import i18n from '@fiverr/i18n/singleton';
import {
    MINUTE_IN_MS,
    HOUR_IN_MS,
    DAY_IN_MS,
    WEEK_IN_MS,
    MONTH_IN_MS,
    YEAR_IN_MS
} from '..';

/**
 * Default translation prefix
 * @type {String}
 */
const DEFAULT_PREFIX = 'general.how_long_ago.';

/**
 * Translate how long ago X
 * @private
 * @param  {String} prefix translation root (with trailing dot where applicable)
 * @param  {String} locale translation locale
 * @param  {String} k      translation key
 * @param  {Number} [c=0]  count
 * @return {String}
 */
const txt = (prefix, locale, k, c = 0) => {
    const params = { count: Math.floor(c) };

    if (locale) {
        params.$scope = locale;
    }

    return i18n.t(`${prefix}${k}`, params);
};

/**
 * Return the time that has passed since Return the time that has passed since a given time in a human readable format.
 * @param  {Date|Number|String} [date] Point in time to describe in words. Ticks can be accepted as a number or string.
 * @param  {Object} [options] Options object.
 * @param  {String} [options.prefix='general.how_long_ago.'] The base string for the translation.
 * @param  {String} [options.locale] The current locale.
 * @return {String} Descriptive, relative time
 *
 * @property {Number} MINUTE_IN_MS Number of milliseconds in a minute
 * @property {Number} HOUR_IN_MS   Number of milliseconds in an hour
 * @property {Number} DAY_IN_MS    Number of milliseconds in a day
 * @property {Number} WEEK_IN_MS   Number of milliseconds in a week
 * @property {Number} MONTH_IN_MS  Number of milliseconds in a month
 * @property {Number} YEAR_IN_MS   Number of milliseconds in a year
 */
export function howLongAgo(date = new Date(), { prefix = DEFAULT_PREFIX, locale } = {}) {
    const now = Date.now();
    const t = now - date;

    /* eslint-disable no-multi-spaces */
    return Number.isNaN(t) ? '' :                                               // invalid
        t < MINUTE_IN_MS   && txt(prefix, locale, 'just_now')                        || // seconds
        t < HOUR_IN_MS     && txt(prefix, locale, 'minutes_ago', (t / MINUTE_IN_MS)) || // minutes
        t < DAY_IN_MS      && txt(prefix, locale, 'hours_ago',   (t / HOUR_IN_MS))   || // hours
        t < WEEK_IN_MS     && txt(prefix, locale, 'days_ago',    (t / DAY_IN_MS))    || // days
        t < MONTH_IN_MS    && txt(prefix, locale, 'weeks_ago',   (t / WEEK_IN_MS))   || // weeks
        t < YEAR_IN_MS     && txt(prefix, locale, 'months_ago',  (t / MONTH_IN_MS))  || // months
                              txt(prefix, locale, 'years_ago',   (t / YEAR_IN_MS));     // years
    /* eslint-enable no-multi-spaces */
}

// Assign used consts to the function
Object.assign(howLongAgo, {
    MINUTE_IN_MS,
    HOUR_IN_MS,
    DAY_IN_MS,
    WEEK_IN_MS,
    MONTH_IN_MS,
    YEAR_IN_MS
});
